import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { ReactNode } from "react";

const useStyles = makeStyles({
  group: {
    display: "inline-flex",
    alignItems: "center",
    height: "28px",
    boxSizing: "border-box",
    padding: "2px",
    gap: "2px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    height: "22px",
    padding: `0 ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    whiteSpace: "nowrap",
    maxWidth: "240px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transitionProperty: "background-color, color, box-shadow",
    transitionDuration: tokens.durationFast,
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  itemOn: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow2,
    "& svg": {
      color: tokens.colorBrandForeground1,
    },
  },
});

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
}

/** 범위·보기 전환의 정본. 아이템 높이 24px(외곽 ~29px). 사이즈 변형 없음. */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: T;
  options: SegmentOption<T>[];
  onChange: (v: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  const styles = useStyles();
  return (
    <div role="tablist" aria-label={ariaLabel} className={mergeClasses(styles.group, className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="tab" aria-selected={on} title={typeof o.label === "string" ? o.label : undefined} className={mergeClasses(styles.item, on && styles.itemOn)} onClick={() => onChange(o.value)}>
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
